import { useEffect, useState } from "react";
import api from "../lib/api";
import { rupee } from "../components/shared";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Boxes, AlertTriangle, Building2, PackageX } from "lucide-react";

export default function InventoryDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/inventory/dashboard/branches").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return <Card className="p-10 bg-card border-border text-center text-muted-foreground">Loading dashboard…</Card>;

  const alerts = data.low_stock_alerts || [];

  return (
    <div className="space-y-6" data-testid="inventory-dashboard">
      {/* Top totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 bg-card border-border" data-testid="invdash-total-value">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Total Stock Value</span><Boxes size={16} className="text-primary" /></div>
          <div className="font-display text-2xl mt-1 text-primary">{rupee(data.total_stock_value)}</div>
        </Card>
        <Card className="p-4 bg-card border-border">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Branches</span><Building2 size={16} className="text-primary" /></div>
          <div className="font-display text-2xl mt-1">{data.branches.length}</div>
        </Card>
        <Card className="p-4 bg-card border-border">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Low Stock</span><AlertTriangle size={16} className="text-orange-300" /></div>
          <div className="font-display text-2xl mt-1 text-orange-300">{data.total_low}</div>
        </Card>
        <Card className="p-4 bg-card border-border">
          <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-wide text-muted-foreground">Out of Stock</span><PackageX size={16} className="text-red-400" /></div>
          <div className="font-display text-2xl mt-1 text-red-400">{data.total_out}</div>
        </Card>
      </div>

      {/* Per-branch cards */}
      <div>
        <h3 className="font-display text-xl mb-3">Stock Value by Branch</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.branches.map((b) => (
            <Card key={b.branch_id} className="p-4 bg-card border-border" data-testid={`invdash-branch-${b.branch_id}`}>
              <div className="flex items-start justify-between">
                <h4 className="font-medium">{b.branch_name}</h4>
                <Building2 size={15} className="text-muted-foreground" />
              </div>
              <div className="font-display text-2xl text-primary mt-2">{rupee(b.stock_value)}</div>
              <p className="text-xs text-muted-foreground mt-1">{b.tracked_products} tracked products</p>
              <div className="flex gap-2 mt-3">
                <Badge variant="outline" className="border-orange-400/40 text-orange-300">Low: {b.low_stock}</Badge>
                <Badge variant="outline" className="border-red-400/40 text-red-400">Out: {b.out_of_stock}</Badge>
              </div>
            </Card>
          ))}
          {!data.branches.length && <Card className="p-6 bg-card border-border text-muted-foreground">No branches.</Card>}
        </div>
      </div>

      {/* Low-stock alerts */}
      <div>
        <h3 className="font-display text-xl mb-3 flex items-center gap-2"><AlertTriangle size={18} className="text-orange-300" /> Low / Out-of-Stock Alerts</h3>
        <Card className="bg-card border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead>Product</TableHead><TableHead>Branch</TableHead>
                <TableHead className="text-right">Current</TableHead><TableHead className="text-right">Min</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((a, i) => (
                <TableRow key={i} className="border-border" data-testid={`invdash-alert-${i}`}>
                  <TableCell className="font-medium">{a.product_name}</TableCell>
                  <TableCell className="text-muted-foreground">{a.branch_name}</TableCell>
                  <TableCell className="text-right">{a.current} {a.unit}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{a.min} {a.unit}</TableCell>
                  <TableCell>{a.out ? <Badge className="bg-red-500/20 text-red-300">OUT</Badge> : <Badge className="bg-orange-500/20 text-orange-300">LOW</Badge>}</TableCell>
                </TableRow>
              ))}
              {!alerts.length && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">All stock levels are healthy. 🎉</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
